import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import ErrorBoundary from 'components/ErrorBoundary';
import { TooltipProvider } from 'components/Tooltip';
import { Flex } from 'components/layout';
import { Typography } from 'components/typography';
import { isWrittenRulesAgentEnabled } from 'contexts/serviceContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { Maybe } from 'types/UtilityTypes';
import {
  BuyRateData,
  InvocationRuleSetSummary,
  QQAgentInvocation,
  QuickQuoteSlim,
} from 'types/agents/QuickQuoteAgent';

import { getAgentInvocationStatusClassName } from '../utils';
import { AgentActionsMenu } from './AgentActionsMenu';
import { BatchLaneDescription } from './BatchLaneDescription';
import { DATMarketInfoSection } from './DATMarketInfoSection';
import { SingleLaneDescription } from './SingleLaneDescription';
import { isFixedFinalPricing } from './helpers';
import {
  AgentReasoningCard,
  AgentReasoningCardFallback,
} from './writtenRules/AgentReasoningCard';
import { OpenInQuickQuoteHandler } from './writtenRules/LaneReasoningDetails';
import { isWrittenRulesInvocation } from './writtenRules/reasoningHelpers';

interface QuickQuoteAgentsProps {
  invocations: QQAgentInvocation[];
  quickQuotesByInvocationId: Record<string, QuickQuoteSlim[]>;
  ruleSetsByInvocationId?: Record<string, InvocationRuleSetSummary>;
  onOpenInQuickQuote?: OpenInQuickQuoteHandler;
  onOpenBatchQuote?: () => void;
  loading: boolean;
  error: Maybe<string>;
}

const noopOpenInQuickQuote: OpenInQuickQuoteHandler = () => {};

const renderQuoteDescription = (
  invocation: QQAgentInvocation
): Maybe<React.JSX.Element> => {
  const buyRateTasks = (
    invocation.taskInvocations?.filter(
      (task) => task.metadata?.buyRateLookupTaskInvocation
    ) ?? []
  ).sort((taskA, taskB) => taskA.step - taskB.step);

  const sellRateTasks = (
    invocation.taskInvocations?.filter(
      (task) => task.metadata?.sellRateCalculationTaskInvocation
    ) ?? []
  ).sort((taskA, taskB) => taskA.step - taskB.step);

  const primaryBuyInvocation =
    buyRateTasks[0]?.metadata?.buyRateLookupTaskInvocation;
  const primarySellInvocation =
    sellRateTasks[0]?.metadata?.sellRateCalculationTaskInvocation;

  if (!primaryBuyInvocation && isFixedFinalPricing(primarySellInvocation)) {
    return <SingleLaneDescription sellRateData={primarySellInvocation} />;
  }

  if (!primaryBuyInvocation) {
    return null;
  }

  // Batch: a single task invocation with lanes[].
  if (primaryBuyInvocation.mode === 'batch' && primaryBuyInvocation.lanes) {
    return (
      <BatchLaneDescription
        buyInvocation={
          primaryBuyInvocation as BuyRateData & {
            lanes: NonNullable<BuyRateData['lanes']>;
          }
        }
        sellRateTasks={invocation.taskInvocations}
      />
    );
  }

  // Other batch modes (older backend shapes, or "batchQuoteId" records) are not currently rendered.
  if (
    primarySellInvocation?.mode === 'batch' ||
    invocation.batchQuoteId !== undefined
  ) {
    return null;
  }

  return (
    <SingleLaneDescription
      buyRateData={primaryBuyInvocation}
      sellRateData={primarySellInvocation}
    />
  );
};

const QuickQuoteAgents = ({
  invocations,
  quickQuotesByInvocationId,
  ruleSetsByInvocationId,
  onOpenInQuickQuote = noopOpenInQuickQuote,
  onOpenBatchQuote,
  loading,
  error,
}: QuickQuoteAgentsProps): Maybe<React.JSX.Element> => {
  const { serviceFeaturesEnabled } = useServiceFeatures();
  const { isQuickQuoteAgentEnabled } = serviceFeaturesEnabled;
  const isWrittenRulesEnabled = isWrittenRulesAgentEnabled(
    serviceFeaturesEnabled
  );

  // Track disabled state by agent ID to sync across sibling invocations
  const [agentDisabledStates, setAgentDisabledStates] = useState<
    Record<number, boolean>
  >(() => {
    const initial: Record<number, boolean> = {};
    invocations.forEach((invocation) => {
      initial[invocation.quickQuoteAgentId] = invocation.agentDisabled;
    });
    return initial;
  });

  // Track which agent IDs have a pending optimistic toggle not yet confirmed by backend
  const pendingToggles = useRef<Set<number>>(new Set());

  // Sync disabled states from polled invocations, skipping agents with in-flight optimistic updates
  useEffect(() => {
    setAgentDisabledStates((prev) => {
      const next = { ...prev };
      let changed = false;
      invocations.forEach((invocation) => {
        const agentId = invocation.quickQuoteAgentId;
        if (
          !pendingToggles.current.has(agentId) &&
          prev[agentId] !== invocation.agentDisabled
        ) {
          next[agentId] = invocation.agentDisabled;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [invocations]);

  const handleAgentDisabledChange = useCallback(
    (agentId: number, disabled: boolean) => {
      pendingToggles.current.add(agentId);
      setAgentDisabledStates((prev) => ({
        ...prev,
        [agentId]: disabled,
      }));
      // Clear the pending flag after the next poll cycle (polling interval is 3 s)
      setTimeout(() => {
        pendingToggles.current.delete(agentId);
      }, 5000);
    },
    []
  );

  if (!isQuickQuoteAgentEnabled) {
    return null;
  }

  if (loading) {
    return (
      <Flex direction='col' gap='sm' className='p-3 w-full'>
        <Typography variant='body-sm'>
          Loading Quick Quote Agent data...
        </Typography>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex direction='col' gap='sm' className='p-3 w-full'>
        <Typography variant='body-sm' className='text-error-main'>
          {error}
        </Typography>
      </Flex>
    );
  }

  return (
    <TooltipProvider>
      <Flex direction='col' gap='sm' className='w-full'>
        {invocations.length === 0 ? (
          <Typography variant='body-sm'>
            No agent data found for this thread.
          </Typography>
        ) : (
          <Flex direction='col' gap='sm' className='w-full'>
            {invocations.length > 1 && (
              <Typography variant='body-sm' className='text-neutral-600'>
                Quick Quote Agent ran {invocations.length} times.
              </Typography>
            )}

            {invocations.map((invocation) => {
              // Written-rules runs explain themselves; everything else keeps the existing box.
              if (
                isWrittenRulesEnabled &&
                isWrittenRulesInvocation(invocation)
              ) {
                return (
                  <ErrorBoundary
                    key={invocation.id}
                    fallback={<AgentReasoningCardFallback />}
                  >
                    <AgentReasoningCard
                      invocation={invocation}
                      ruleSet={ruleSetsByInvocationId?.[String(invocation.id)]}
                      agentDisabled={
                        agentDisabledStates[invocation.quickQuoteAgentId] ??
                        invocation.agentDisabled
                      }
                      onAgentDisabledChange={handleAgentDisabledChange}
                      onOpenInQuickQuote={onOpenInQuickQuote}
                      onOpenBatchQuote={onOpenBatchQuote}
                    />
                  </ErrorBoundary>
                );
              }

              const quoteDescription = renderQuoteDescription(invocation);

              return (
                <Flex
                  key={invocation.id}
                  direction='col'
                  gap='sm'
                  className='py-2 px-2.5 bg-neutral-100 rounded-sm border border-neutral-700 w-full'
                >
                  <Flex align='center' justify='between' className='w-full'>
                    <Typography variant='body-sm' weight='semibold'>
                      Quick Quote Agent
                    </Typography>

                    <Flex align='center' gap='sm'>
                      {/* Invocation status */}
                      <Typography
                        variant='body-xxs'
                        weight='medium'
                        as='span'
                        className={getAgentInvocationStatusClassName(
                          invocation.status
                        )}
                      >
                        {invocation.status}
                      </Typography>

                      <AgentActionsMenu
                        agentId={invocation.quickQuoteAgentId}
                        disabled={
                          agentDisabledStates[invocation.quickQuoteAgentId] ??
                          invocation.agentDisabled
                        }
                        onDisabledChange={handleAgentDisabledChange}
                      />
                    </Flex>
                  </Flex>

                  {quoteDescription}

                  <DATMarketInfoSection
                    quickQuotes={quickQuotesByInvocationId[invocation.id]}
                  />

                  {invocation.error && (
                    <Flex direction='col' gap='xs'>
                      <Typography variant='body-xs' className='text-error-main'>
                        Agent encountered an error. Quote below using Quick
                        Quote.
                      </Typography>
                    </Flex>
                  )}
                </Flex>
              );
            })}
          </Flex>
        )}
      </Flex>
    </TooltipProvider>
  );
};

export default QuickQuoteAgents;
